// Ember core logic — the reframe engine.
// Every cognitive signal the user gives produces an immediate, specific return.
// This is deliberately hand-written pattern matching + small-town emotional grammar.
// No generic "I hear you" language. The point is to make the clarifying anger legible.

import type {
  Season, ScarcityKind, TradeoffDomain, UrgencySource, Card, Circle, SignalEvent, ReframeReturn
} from './types'
import { SEASON_LABEL, SEASON_HINT, SCARCITY_LABEL, TRADEOFF_LABEL, URGENCY_LABEL } from './types'

// === Human Pace Calendar primitives ===

export const SEASON_ORDER: Season[] = ['deep_winter', 'thaw', 'planting', 'high_summer', 'harvest', 'back_to_dark']

export function seasonShort(s: Season): string {
  return ({
    deep_winter: 'WINTER',
    thaw: 'THAW',
    planting: 'PLANT',
    high_summer: 'SUMMER',
    harvest: 'HARVEST',
    back_to_dark: 'DARK',
  })[s]
}

// === The reframe engine: visible return for every signal ===

export function getPressureReturn(seasons: Season[]): ReframeReturn {
  if (seasons.length === 0) {
    return {
      title: 'The calendar is still empty.',
      body: 'Where this tension actually collides with real life in your town is the first real piece of information.',
    }
  }
  const labels = seasons.map(s => SEASON_LABEL[s])
  const hints = seasons.map(s => SEASON_HINT[s])

  if (seasons.includes('deep_winter') && seasons.length > 1) {
    return {
      title: 'This lands on winter and on something else.',
      body: `You are asking people to carry this through the long dark *and* through ${labels.filter((_, i) => seasons[i] !== 'deep_winter').join(' and ')}. Most towns treat winter as the season that is already full of invisible work.`,
      insight: 'The clarifying anger may be about the refusal to let winter be winter.',
    }
  }
  if (seasons.length >= 3) {
    return {
      title: `${seasons.length} seasons are already carrying load.`,
      body: `This tension is not a single-season problem. It is pressing on ${labels.join(' • ')}. The scarcity you feel is at least partly arithmetic: there are only so many people who can be in two places in the same body.`,
      insight: 'Optimization here often means asking the same small group to be everywhere at once.',
    }
  }
  return {
    title: `This presses hardest in ${labels[0]}.`,
    body: hints[0],
    insight: 'The question is not "how do we add capacity here" but "what are we already doing in this season that we are unwilling to name as already enough?"',
  }
}

export function getScarcityReturn(kinds: ScarcityKind[], intensity: number): ReframeReturn {
  if (kinds.length === 0) return { title: 'Name the scarcity.', body: 'What is actually in short supply when this tension rises?' }

  const primary = kinds[0]
  const primaryLabel = SCARCITY_LABEL[primary]

  if (kinds.includes('goodwill') && kinds.includes('people')) {
    return {
      title: 'Goodwill and people are both scarce.',
      body: 'When the town feels it is running out of both the willingness to show up and the bodies to show up, the usual move is to create one more event or committee to "build engagement". That move consumes the last of both.',
      insight: 'The anger may be clarifying that you are being asked to manufacture belonging on top of the work of belonging.',
    }
  }
  if (kinds.includes('goodwill')) {
    return {
      title: 'Goodwill is the resource being spent.',
      body: `You named "${primaryLabel.toLowerCase()}". In small places this is often the real limit long before money or hours. People will drive twenty minutes for a funeral but not for another meeting about how to get more people to drive twenty minutes.`,
      insight: 'Goodwill is not replenished by activity. It is replenished by things that feel like they respected the life that was already here.',
    }
  }
  if (intensity >= 8) {
    return {
      title: `The scarcity feels almost total (${intensity}/10).`,
      body: `When ${primaryLabel.toLowerCase()} registers this high, the nervous system of the town is in a threat state. In that state, almost every proposal for "doing something" will be heard as "do more with what we already do not have enough of."`,
      insight: 'Relief rarely comes from solving the surface problem. It comes from naming the threat state out loud so the body can stop bracing.',
    }
  }
  return {
    title: primaryLabel,
    body: 'This is the specific shortage the tension is creating or revealing. Optimization language usually tries to turn this into a logistics problem. It is rarely only that.',
  }
}

export function getTradeoffReturn(tradeoffs: (TradeoffDomain | string)[]): ReframeReturn {
  if (tradeoffs.length === 0) return { title: 'What gets dropped when we push harder on this?', body: 'The town is already choosing. The question is whether we will say it out loud.' }

  const named = tradeoffs.map(t => typeof t === 'string' ? t : TRADEOFF_LABEL[t])

  if (tradeoffs.includes('winter_rest') || tradeoffs.some(t => typeof t === 'string' && /winter/i.test(t))) {
    return {
      title: 'Winter rest is on the table.',
      body: `You are willing to spend the long dark on this. That is a choice almost no one will argue with in public and almost everyone will feel in their body. Winter is the only season left in many towns that still belongs to the people who live there year-round.`,
      insight: 'The clarifying anger is often about the last remaining time that is not yet colonized by the requirement to perform community for someone else.',
    }
  }
  if (tradeoffs.includes('youth_presence') || tradeoffs.includes('spontaneous_gatherings')) {
    return {
      title: 'The next generation and the unplanned are being spent.',
      body: `When "${named[0]}" is what quietly disappears, the town is solving for the visible problem by removing the exact conditions that make young people and long-term residents want to remain. This is the optimization trap in its purest form.`,
      insight: 'The anger clarifies a value: we still believe a place is good when people can simply be in it without a reason or a role.',
    }
  }
  return {
    title: 'The hidden cost is now named.',
    body: `To move on this, the town is prepared to lose or further diminish: ${named.join(' • ')}. Most "solutions" never make this cost explicit. You just did.`,
    insight: 'Once named, the question "is this worth it?" becomes a real question instead of a foregone conclusion.',
  }
}

export function getUrgencyReturn(source: UrgencySource, note?: string): ReframeReturn {
  const label = URGENCY_LABEL[source]

  if (source === 'habit_performance') {
    return {
      title: 'The driver is "this is how we have always responded".',
      body: 'Habit performance is the most expensive form of urgency because it feels like virtue. It is very difficult to slow down without feeling like you are betraying the people who taught you that caring means moving fast.',
      insight: 'The clarifying anger may be grief for a time when the town trusted its own pace more than its own performance.',
    }
  }
  if (source === 'fear_of_appearing_idle') {
    return {
      title: 'The real clock is the fear of being seen as not caring enough.',
      body: 'This is the optimization trap wearing a moral mask. The town keeps adding motion so that no one can say it has given up. The people who are most exhausted by this are often the ones who care the most.',
      insight: 'Sometimes the most caring act is to let a visible "nothing new" stand for a season so that the real caring can be seen again.',
    }
  }
  if (source === 'grant_deadline' || source === 'vocal_complaint') {
    return {
      title: `The urgency is coming from ${source === 'grant_deadline' ? 'an outside clock' : 'a loud voice'}.`,
      body: label + (note ? ` — ${note}` : ''),
      insight: 'External or vocal urgency is not automatically illegitimate. It is simply not automatically the town\'s own nervous system. The work is to feel the difference.',
    }
  }
  return {
    title: label,
    body: note || 'You have located the actual source of the "must be now". This is information the town almost never gives itself.',
  }
}

export function getAngerReturn(angerText: string): ReframeReturn {
  if (!angerText || angerText.trim().length < 12) {
    return {
      title: 'The anger has not yet been turned into language.',
      body: 'Clarifying anger is the signal that something still matters enough to hurt. Until it is spoken, the town will continue to treat the hurt as a problem to be solved rather than information to be received.',
    }
  }
  const trimmed = angerText.trim()
  return {
    title: 'You have given the anger a sentence.',
    body: trimmed.endsWith('.') ? trimmed : trimmed + '.',
    insight: 'This is no longer "we are angry." It is "we are angry because we still believe [this] is worth protecting." That is a completely different starting point for any conversation.',
  }
}

export function getBoundaryReturn(boundary: string, strength: number): ReframeReturn {
  if (!boundary || boundary.trim().length < 10) {
    return {
      title: 'A human pace boundary has not yet been declared.',
      body: 'Without an explicit "we will not" or "we will protect this time", the ritual ends in the same place every other conversation ends: a general wish to do better.',
    }
  }
  const strong = strength >= 4
  return {
    title: strong 
      ? 'A costly boundary has been named.' 
      : 'A boundary has been placed on the calendar.',
    body: boundary.trim(),
    insight: strong 
      ? 'Because this boundary will actually cost something (visibility, money, someone\'s feelings), it has a chance of producing the relief the town keeps seeking in the wrong direction.'
      : 'A softer boundary is still information. The town now knows where it is unwilling to fully slow.',
  }
}

// === The full card reframe (the "output") ===

export function buildReframedStatement(card: Partial<Card>): string {
  const title = card.tensionTitle || 'A tension in the life of this place'
  const seasons = (card.pressureSeasons || []).map(s => SEASON_LABEL[s]).join(' and ')
  const anger = card.clarifyingAnger || 'something here still matters enough to hurt'
  const boundary = card.paceBoundary || 'we have not yet chosen what we will refuse to accelerate'

  return `${title}. This tension presses on ${seasons || 'the whole year'}. The scarcity named is real. The tradeoffs are being paid whether we speak them or not. The urgency is partly ${card.urgencySource ? URGENCY_LABEL[card.urgencySource].toLowerCase() : 'unexamined'}. 

What the anger clarifies: ${anger}

The human pace choice: ${boundary}

This card is not a plan. It is a record that the town once looked at the cost of constant motion and chose, for a season, to let the clarifying anger do its work instead.`
}

// === Local persistence helpers (MVP works beautifully offline) ===

const LS = {
  circle: 'ember_circle',
  cards: 'ember_cards',
  keeper: 'ember_keeper_hash',
  lastCode: 'ember_last_code',
}

export function getOrCreateLocalCircle(): { circle: Circle; isNew: boolean } {
  const raw = localStorage.getItem(LS.circle)
  if (raw) {
    return { circle: JSON.parse(raw), isNew: false }
  }
  const circle: Circle = {
    id: 'c_' + crypto.randomUUID().slice(0, 12),
    code: 'LOCAL-' + Math.floor(100 + Math.random() * 900),
    name: 'The People Who Stayed',
    keeperHash: 'local-' + crypto.randomUUID().slice(0, 10),
    createdAt: new Date().toISOString(),
  }
  localStorage.setItem(LS.circle, JSON.stringify(circle))
  localStorage.setItem(LS.keeper, circle.keeperHash)
  return { circle, isNew: true }
}

export function getLocalCards(circleId: string): Card[] {
  const all = JSON.parse(localStorage.getItem(LS.cards) || '[]') as Card[]
  return all.filter(c => c.circleId === circleId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function saveLocalCard(card: Card) {
  const all = JSON.parse(localStorage.getItem(LS.cards) || '[]') as Card[]
  const idx = all.findIndex(c => c.id === card.id)
  if (idx >= 0) all[idx] = card
  else all.push(card)
  localStorage.setItem(LS.cards, JSON.stringify(all))
}

export function isKeeper(circle: Circle): boolean {
  const h = localStorage.getItem(LS.keeper)
  return !!h && h === circle.keeperHash
}

export function setKeeperHash(hash: string) {
  localStorage.setItem(LS.keeper, hash)
}

// === Analytics event builder (what we write to Analytics Engine and to D1 signal_events) ===

export function buildSignalPayloads(card: Partial<Card> & { circleId: string }, cardId?: string) {
  const base = { circleId: card.circleId, cardId }
  const events: Array<{ signalType: SignalEvent['signalType']; payload: Record<string, unknown> }> = []

  if (card.pressureSeasons?.length) {
    events.push({ signalType: 'pressure', payload: { seasons: card.pressureSeasons } })
  }
  if (card.scarcityKinds?.length) {
    events.push({ 
      signalType: 'scarcity', 
      payload: { kinds: card.scarcityKinds, intensity: card.scarcityIntensity } 
    })
  }
  if (card.tradeoffs?.length) {
    events.push({ signalType: 'tradeoff', payload: { domains: card.tradeoffs } })
  }
  if (card.urgencySource) {
    events.push({ signalType: 'urgency', payload: { source: card.urgencySource } })
  }
  if (card.clarifyingAnger) {
    events.push({ signalType: 'anger', payload: { hasText: true, length: card.clarifyingAnger.length } })
  }
  if (card.paceBoundary) {
    events.push({ 
      signalType: 'boundary', 
      payload: { strength: card.boundaryStrength, hasText: true } 
    })
  }
  if (typeof card.reliefScore === 'number') {
    events.push({ signalType: 'relief', payload: { score: card.reliefScore, hadBoundary: !!card.paceBoundary } })
  }
  return events.map(e => ({ ...base, ...e }))
}

// === Dashboard insight engine (the profound part) ===

export function computeDashboardInsights(cards: Card[]) {
  if (cards.length === 0) {
    return {
      totalCards: 0,
      avgRelief: 0,
      boundaryEffect: null as any,
      chronicSeasons: [] as Season[],
      scarcityPattern: 'No signals yet.',
      angerThemes: [] as string[],
      roiNote: 'The almanac is empty. The first pacing will teach the town what it has been paying.',
    }
  }

  const total = cards.length
  const avgRelief = cards.reduce((s, c) => s + c.reliefScore, 0) / total

  const withBoundary = cards.filter(c => c.paceBoundary && c.paceBoundary.trim().length > 12)
  const without = cards.filter(c => !c.paceBoundary || c.paceBoundary.trim().length < 12)

  const boundaryEffect = withBoundary.length && without.length ? {
    withAvg: withBoundary.reduce((s, c) => s + c.reliefScore, 0) / withBoundary.length,
    withoutAvg: without.reduce((s, c) => s + c.reliefScore, 0) / without.length,
    countWith: withBoundary.length,
  } : null

  // Chronic seasons: seasons that appear in > 40% of cards
  const seasonCounts: Record<Season, number> = { deep_winter: 0, thaw: 0, planting: 0, high_summer: 0, harvest: 0, back_to_dark: 0 }
  cards.forEach(c => c.pressureSeasons.forEach(s => { seasonCounts[s]++ }))
  const chronicSeasons = (Object.keys(seasonCounts) as Season[]).filter(s => seasonCounts[s] / total >= 0.4)

  // Simple scarcity pattern language
  const hasGoodwill = cards.filter(c => c.scarcityKinds.includes('goodwill')).length
  const scarcityPattern = hasGoodwill / total > 0.5 
    ? 'Goodwill scarcity appears in more than half the tensions. This is rarely solved by adding more activity.'
    : 'The dominant scarcity is material or attentional. The town is still able to name it without shame.'

  // Extract short anger phrases (first clause-ish)
  const angerThemes = cards
    .map(c => c.clarifyingAnger?.split(/[.!?]/)[0]?.trim())
    .filter(Boolean)
    .slice(0, 5) as string[]

  const roiNote = boundaryEffect && boundaryEffect.withAvg - boundaryEffect.withoutAvg > 1.5
    ? `When an explicit human pace boundary was declared, average relief was ${(boundaryEffect.withAvg - boundaryEffect.withoutAvg).toFixed(1)} points higher. Your neighbors are telling you, in their own data, that the return on cognition comes from deliberate refusal more than from better execution.`
    : 'The pattern is still forming. Keep going. The almanac becomes truthful only after the town has trusted it with several real tensions.'

  return {
    totalCards: total,
    avgRelief: Math.round(avgRelief * 10) / 10,
    boundaryEffect,
    chronicSeasons,
    scarcityPattern,
    angerThemes,
    roiNote,
  }
}

// === Seed example cards so a new circle is never empty ===

export function getSeedCards(circleId: string): Card[] {
  const now = new Date().toISOString()
  return [
    {
      id: 'seed-1',
      circleId,
      tensionTitle: 'We cannot keep the volunteer fire department staffed',
      tensionBody: 'The old guard is aging out. The younger people who grew up here are working two jobs or have left. We keep having recruitment drives that produce one or two people who then burn out in 18 months.',
      pressureSeasons: ['harvest', 'back_to_dark', 'deep_winter'],
      scarcityKinds: ['people', 'goodwill', 'energy'],
      scarcityIntensity: 8,
      tradeoffs: ['winter_rest', 'the_ones_who_stay'],
      urgencySource: 'habit_performance',
      clarifyingAnger: 'This anger clarifies that we still believe a place that cannot take care of its own fires has already stopped being a community.',
      paceBoundary: 'We will not hold any additional recruitment events or "awareness" campaigns between the first hard frost and the spring thaw. Winter belongs to the people who are still here. If the department shrinks, that is information we will sit with instead of medicating.',
      boundaryStrength: 4,
      reliefScore: 7,
      reframedStatement: 'We cannot keep the volunteer fire department staffed. This tension presses on Harvest and the long Dark. The scarcity named is real. The tradeoffs are being paid whether we speak them or not. The urgency is partly we have always moved fast when something feels broken.\n\nWhat the anger clarifies: This anger clarifies that we still believe a place that cannot take care of its own fires has already stopped being a community.\n\nThe human pace choice: We will not hold any additional recruitment events or "awareness" campaigns between the first hard frost and the spring thaw. Winter belongs to the people who are still here. If the department shrinks, that is information we will sit with instead of medicating.\n\nThis card is not a plan. It is a record that the town once looked at the cost of constant motion and chose, for a season, to let the clarifying anger do its work instead.',
      isPublished: true,
      createdAt: now,
    },
    {
      id: 'seed-2',
      circleId,
      tensionTitle: 'Main Street dies after six o\'clock',
      tensionBody: 'Every "revitalization" effort adds another evening event. The people who actually live above or behind the stores are the ones who have to move their cars, endure the noise, and still open at 7am. The ones who show up for the events drive in from elsewhere.',
      pressureSeasons: ['high_summer', 'harvest'],
      scarcityKinds: ['goodwill', 'energy', 'the_ones_who_stay' as any],
      scarcityIntensity: 7,
      tradeoffs: ['main_street_daily_life', 'unpaid_care', 'the_ones_who_stay'],
      urgencySource: 'vocal_complaint',
      clarifyingAnger: 'This anger clarifies that we still value the slow, unremarkable daily life of the street more than we value being seen as a place where things are always happening.',
      paceBoundary: 'We will place a hard stop on new evening "activation" programming from November through March. The winter street is allowed to be mostly dark and mostly quiet. The people who live here get their evenings back.',
      boundaryStrength: 5,
      reliefScore: 8,
      reframedStatement: 'Main Street dies after six o\'clock. This tension presses on High Summer and Harvest. The scarcity named is real. The tradeoffs are being paid whether we speak them or not. The urgency is partly a loud voice (or several) saying we must act now.\n\nWhat the anger clarifies: This anger clarifies that we still value the slow, unremarkable daily life of the street more than we value being seen as a place where things are always happening.\n\nThe human pace choice: We will place a hard stop on new evening "activation" programming from November through March. The winter street is allowed to be mostly dark and mostly quiet. The people who live here get their evenings back.\n\nThis card is not a plan. It is a record that the town once looked at the cost of constant motion and chose, for a season, to let the clarifying anger do its work instead.',
      almanacNote: 'This session surfaced a chronic pattern: the town has been using summer programming as a grief regulator for the loss of everyday main street life. The winter boundary produced unusually high relief because it directly contradicted the "we must be seen as vibrant" reflex. Watch for repeat pressure on "activation" in future sessions.',
      isPublished: true,
      createdAt: new Date(Date.now() - 1000 * 3600 * 24 * 11).toISOString(),
    },
  ]
}
